import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL
const BUILD_NUMBER = process.env.REACT_APP_CODEBUILD_BUILD_NUMBER
const VERSION_NUMBER = process.env.REACT_APP_VERSION

export const ApiContext = createContext(null)

// Sparse fieldsets params (see civicrush-backend
// docs/sparse-fieldsets-adoption.md). Omitting `fields` renders all
// scalars; omitting `include` renders all includes. Scopes: event, user,
// group, notification.
const sparse = (scope, fields, include) => ({
  params: { fields: { [scope]: fields }, include: { [scope]: include } },
})

const eventSparse = (fields, include) => sparse('event', fields, include)

// Merges per-scope sparse configs into one axios config (multi-scope
// requests like search and notifications).
const mergeSparse = (...configs) => ({
  params: {
    fields: Object.assign({}, ...configs.map(c => c.params.fields)),
    include: Object.assign({}, ...configs.map(c => c.params.include)),
  },
})

const eventCardSparseFields =
  'id,title,drafted_at,event_end_date,event_start_date,slug,service_hours,remote,is_private,volunteers_count,volunteers_needed,event_type,likes_count,share_count,current_user_admin,is_user_admin'
const eventCardSparseInclude =
  'media_attachments,categories,location,event_service_hour_allocations'

// Event detail (show.jsx, form.jsx edit init, useSponsorPledgeMinimumCiv,
// SelfCheckInFlow, lib/event.js, files.js calendar export, TeamSidebar
// participant report): the union of every getEvent caller's reads.
// volunteers_start_date/volunteers_end_date are client-computed in form.jsx,
// not API fields. check_in_token stays power-user-gated server-side
// regardless of params.
const eventDetailSparseFields =
  'id,title,slug,event_type,event_start_date,event_end_date,drafted_at,details,volunteers_details,hashtags,volunteers_needed,service_hours,remote,is_private,check_in_token,volunteer_pay_forward,effective_minimum_required_pledge_amount,minimum_required_pledge_amount,event_rating_average,participant_signup'
const eventDetailSparseInclude =
  'event_service_hour_allocations,volunteer,participants,user,admins,location,tasks,media_attachments,categories,groups'

// User rows (UserRow, FollowButton, admin pickers): name/nickname/avatar
// plus the follow/block state FollowButton reads and the followers_count
// the profile counters read.
const userCardSparse = sparse(
  'user',
  'id,name,nickname,followers_count,current_user_follows,current_user_blocks',
  'media_attachment',
)

// Group directory rows; mirrors the mobile group card shape (includes the
// opt-in current_user_waitlist and the parent for "part of" labels).
const groupCardSparse = sparse(
  'group',
  'id,name,image_url,user_count,is_private,description,user_id,slug,activities_count,current_user_associated,current_user_admin,current_user_waitlist',
  'parent,user',
)

// Group members rows; member_join is the backend's join-date key.
const memberCardSparse = sparse(
  'user',
  'id,name,nickname,member_join',
  'media_attachment',
)

// The notifications dropdown reads id/notify_type/created_at/read_at plus
// the nested actor/target/second_target blocks, which are trimmed with the
// user/group/event scopes (Event targets keep the event card shape).
const notificationSparse = mergeSparse(
  sparse(
    'notification',
    'id,notify_type,created_at,read_at',
    'actor,target,second_target',
  ),
  userCardSparse,
  groupCardSparse,
  eventSparse(eventCardSparseFields, eventCardSparseInclude),
)

// Global search returns { users, events, groups }: one scope each.
const searchSparse = mergeSparse(
  eventSparse(eventCardSparseFields, eventCardSparseInclude),
  userCardSparse,
  groupCardSparse,
)

/**
 * Internal hook that holds API/auth state. Only used by ApiProvider.
 * All other components use useApi() which reads from context.
 */
function useApiInternal() {
  const [authHeaders, setAuthHeaders] = useState(() => {
    try {
      const stored = localStorage.getItem('authHeaders')
      return stored ? JSON.parse(stored) : {}
    } catch (e) {
      return {}
    }
  })

  // make the interceptor synchronous so it exists before any React effects run - gets rid of race conditions
  const [api] = useState(() => {
    const instance = axios.create({
      baseURL: API_BASE_URL,
    })
    instance.interceptors.request.use(
      config => {
        let storedHeaders = {}
        try {
          const stored = localStorage.getItem('authHeaders')
          storedHeaders = stored ? JSON.parse(stored) : {}
        } catch (e) {
          storedHeaders = {}
        }
        config.headers = { ...config.headers, ...storedHeaders }
        return config
      },
      error => Promise.reject(error),
    )
    return instance
  })

  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const stored = localStorage.getItem('currentUser')
      return stored ? JSON.parse(stored) : {}
    } catch (e) {
      return {}
    }
  })

  // Sync authHeaders state to api.defaults.headers AND localStorage
  useEffect(() => {
    if (authHeaders && Object.keys(authHeaders).length > 0) {
      Object.keys(authHeaders).forEach(key => {
        api.defaults.headers.common[key] = authHeaders[key]
      })
      localStorage.setItem('authHeaders', JSON.stringify(authHeaders))
    } else {
      const authKeys = ['access-token', 'client', 'uid', 'expiry', 'token-type']
      authKeys.forEach(key => {
        delete api.defaults.headers.common[key]
      })
      localStorage.removeItem('authHeaders')
    }
  }, [authHeaders, api])

  useEffect(() => {
    if (currentUser && Object.keys(currentUser).length > 0) {
      localStorage.setItem('currentUser', JSON.stringify(currentUser))
    } else {
      localStorage.removeItem('currentUser')
    }
  }, [currentUser])

  const get = useCallback(
    (url, config) =>
      api
        .get(url, config)
        .then(response => response)
        .catch(error => {
          return {
            data: undefined,
            status: error?.response?.status,
            error,
          }
        }),
    [api],
  )
  const post = (url, data, config) =>
    api
      .post(url, data, config)
      .catch(error => ({
        success: false,
        error,
        errors: error?.response?.data?.errors,
      }))
      .then(response => {
        // eslint-disable-next-line no-console
        console.log('API POST', url, response)
        return response
      })
  const put = (url, data, config) =>
    api
      .put(url, data, config)
      .catch(error => ({
        success: false,
        error,
        errors: error?.response?.data?.errors,
      }))
      .then(response => {
        // eslint-disable-next-line no-console
        console.log('API PUT', url, response)
        return response
      })
  const patch = (url, data, config) =>
    api
      .patch(url, data, config)
      .catch(error => ({
        success: false,
        error,
        errors: error?.response?.data?.errors,
      }))
      .then(response => {
        // eslint-disable-next-line no-console
        console.log('API PATCH', url, response)
        return response
      })
  const del = (url, data) =>
    api
      .delete(url, data)
      .then(response => {
        // eslint-disable-next-line no-console
        console.log('API DELETE', url, response)
        return response
      })
      .catch(error => {
        return { data: undefined, status: error?.response?.status, error }
      })

  const getUser = useCallback(
    ({ nickname, userId }) => {
      if (nickname) {
        return get(
          `users/nickname.json?nickname=${encodeURIComponent(
            nickname.replace('@', ''),
          )}`,
        )
      }
      return get(`users/${userId}.json`)
    },
    [get],
  )

  const getCurrentUser = useCallback(
    () => getUser({ userId: currentUser?.id }),
    [currentUser, getUser],
  )

  const isSessionValid = useCallback(async () => {
    let valid = false
    if (currentUser?.id) {
      try {
        const response = await getCurrentUser()
        if (response.status === 200) {
          setCurrentUser(response.data)
          valid = true
        }
      } catch (_err) {
        // 401 or network error: treat session as invalid, don't rethrow (avoids error overlay)
        valid = false
      }
    }
    return valid
  }, [api, currentUser, getCurrentUser])

  const signInWithEmail = useCallback(
    async ({ email, password, locale }) =>
      post(`api/v1/auth/sign_in?locale=${locale}`, {
        email,
        password,
        web_app_build_id: BUILD_NUMBER,
        web_app_version_id: VERSION_NUMBER,
      }).then(async response => {
        if (response.status === 200) {
          if (response.headers && response.headers['access-token']) {
            const headers = {
              'access-token': response.headers['access-token'],
              'token-type': 'Bearer',
              client: response.headers.client,
              expiry: response.headers.expiry,
              uid: response.headers.uid,
            }
            setAuthHeaders(headers)
            // const userResponse = await getUser({
            //   userId: response.data?.data?.id,
            // })
            // if (userResponse.status === 200) {
            //   setCurrentUser(userResponse.data)
            // }
            setCurrentUser(response.data.data)
          }
        }
        return response
      }),
    [],
  )

  const signOut = useCallback(() => {
    // Clear state
    setAuthHeaders({})
    setCurrentUser({})

    // Clear localStorage
    localStorage.removeItem('authHeaders')
    localStorage.removeItem('currentUser')

    // Clear axios headers
    const authKeys = ['access-token', 'client', 'uid', 'expiry', 'token-type']
    authKeys.forEach(key => {
      delete api.defaults.headers.common[key]
    })
  }, [api])

  const signInWithFacebookResponse = useCallback(async response => {
    const { token, user } = response.data
    const headers = {
      'access-token': token['access-token'],
      'token-type': 'Bearer',
      client: token.client,
      expiry: token.expiry,
      uid: token.uid,
    }
    setAuthHeaders(headers)
    setCurrentUser(user)
    return response
  }, [])

  const getEventCategories = () => get('categories.json')

  const getServiceHourCategories = () => get('service_hour_categories.json')

  const getPossibleAdmins = ({ groups, query = '', page = 1 }) => {
    // If groups are provided, the search will only return
    // users from those groups. Otherwise, it will return all users
    // A private event must search within only its groups.
    const params = new URLSearchParams({ page, q: query })
    if (groups) {
      groups.forEach(group => params.append('group_ids[]', group.id))
    }
    return get(`users/possible_admins.json?${params}`, userCardSparse)
  }

  const getUserSuggestions = ({ search = '' }) =>
    api
      .get(`users/autocomplete.json`, { params: { q: search ?? '' } })
      .then(response => {
        // eslint-disable-next-line no-console
        console.log(response.data)
        return response.data.users
      })
      .catch(() => [])

  const sendBlockedReason = ({ id }) => put(`user_blocks/${id}`)

  const getVersions = () => get('app_versions')

  const getGroupImpact = ({ groupId }) => get(`groups/${groupId}/dashboard`)

  const getGroupActivities = () => get('dashboard/group_activities')

  const getTodaysEvents = () =>
    get(
      'dashboard/today.json',
      eventSparse(
        'id,title,event_start_date,event_type,slug,volunteer_pay_forward',
        'media_attachments',
      ),
    )

  const getMonthlyEvents = () =>
    get(
      'dashboard/this_month.json',
      eventSparse(
        'id,title,event_start_date,event_type,slug,volunteer_pay_forward',
        'media_attachments',
      ),
    )

  const getMyEvents = () => get('events/my_events.json')

  const getMyVolunteering = () => get('volunteers/my_volunteering.json')

  const getCurrentUserGroupEvents = useCallback(
    () =>
      get(
        `users/${currentUser.id}/group_events.json`,
        eventSparse(
          'id,title,event_start_date,event_end_date,slug',
          'media_attachments',
        ),
      ),
    [get, currentUser],
  )

  const getCurrentUserFollowingEvents = useCallback(
    () =>
      get(
        `users/${currentUser.id}/following_users_events.json`,
        eventSparse(
          'id,title,event_start_date,event_end_date,slug',
          'media_attachments,display_users',
        ),
      ),
    [get, currentUser],
  )

  const getCivDonations = useCallback(() => get('civ_donations.json'), [get])

  const getSponsorshipOpportunities = () =>
    get(
      'events/sponsorship_opportunities.json',
      eventSparse(
        'id,title,event_start_date,event_end_date,slug',
        'media_attachments,display_users',
      ),
    )

  const updateUser = (id, user) => put(`users/${id}.json`, user)

  const updateUserEmail = ({ id, user }) => put(`users/${id}`, { user })

  const updateUserQuote = ({ id, user }) => put(`users/${id}`, { user })

  const emailAvailable = ({ email }) =>
    get(`email_available?email=${encodeURIComponent(email)}`)

  const usernameAvailable = ({ username }) =>
    get(`nickname_available?nickname=${encodeURIComponent(username)}`)

  const businessSlugAvailable = ({ slug, businessId }) => {
    const params = new URLSearchParams({ slug })
    if (businessId != null) params.set('business_id', businessId)
    return get(`businesses/slug_available?${params.toString()}`)
  }

  const getAssociations = () => get('groups.json', groupCardSparse)

  const getSearchedAssociations = () => get('groups.json', groupCardSparse)

  const joinAssociation = ({ id }) => put(`groups/${id}/join`)

  const unjoinAssociation = ({ id }) => put(`groups/${id}/unjoin`)

  const getGroup = ({ id }) => get(`groups/${id}.json`)

  const getGroupEvents = ({ id, page, past }) =>
    get(
      `groups/${id}/events.json?page=${page}&past=${past}`,
      eventSparse(eventCardSparseFields, eventCardSparseInclude),
    )

  const getGroupEngagementsPDFData = ({ id }) =>
    get(`groups/${id}/pdf_events.json`)

  // page/q ride in params (they were previously passed as the axios config
  // and silently dropped).
  const getGroupMembers = ({ id, page, q }) =>
    get(`groups/${id}/members`, {
      params: { ...memberCardSparse.params, page, q },
    })

  const createGroup = () => post('groups')

  const updateGroup = ({ id }) => put(`groups/${id}`)

  const deleteGroup = ({ id }) => del(`groups/${id}`)

  const joinGroup = ({ id }) => put(`groups/${id}/join`)

  const leaveGroup = ({ id }) => put(`groups/${id}/unjoin`)

  const addEventAdmin = ({ eventId, userId }) =>
    put(`events/${eventId}/add_admin.json`, { user_id: userId })

  const removeEventAdmin = ({ eventId, userId }) =>
    put(`events/${eventId}/remove_admin.json`, { user_id: userId })

  const addGroupAdmin = ({ id }) => put(`groups/${id}/add_admin`)

  const removeGroupAdmin = ({ id }) => put(`groups/${id}/remove_admin`)

  const requestEventAdminAccess = ({ eventId }) =>
    put(`events/${eventId}/request_admin_access.json`)

  const blockUserFromGroup = ({ id, userId }) =>
    put(`groups/${id}/block`, { user_id: userId })
  const unblockUserFromGroup = ({ id, userId }) =>
    put(`groups/${id}/unblock`, { user_id: userId })
  const acceptUserFromGroup = ({ id, userId }) =>
    put(`groups/${id}/accept_waitlist`, { user_id: userId })
  const declineUserFromGroup = ({ id, userId }) =>
    put(`groups/${id}/decline_waitlist`, { user_id: userId })

  // q rides in params (it was previously passed as the axios config and
  // silently dropped).
  const search = ({ q }) =>
    get('search.json', { params: { ...searchSparse.params, q } })

  const volunteerUserRating = ({ eventId, volunteerId }) =>
    put(`events/${eventId}/volunteers/${volunteerId}/rate_user`)

  const volunteerEventRating = ({ eventId, volunteerId }) =>
    put(`events/${eventId}/volunteers/${volunteerId}/rate_event`)

  const participantEventRating = ({ eventId, participantId }) =>
    put(`events/${eventId}/participants/${participantId}/rate_event`)

  const getUserEvents = ({ id }) =>
    get(
      `users/${id}/events`,
      eventSparse(eventCardSparseFields, eventCardSparseInclude),
    )

  const getUserVolunteeredEvents = ({ id }) =>
    get(
      `users/${id}/volunteering_events`,
      eventSparse(eventCardSparseFields, eventCardSparseInclude),
    )

  const getUserEngagementEvents = ({ id }, past = false) => {
    const config = eventSparse(
      'id,title,event_start_date,event_end_date,remote,volunteers_count,volunteers_needed,slug',
      'media_attachments,location',
    )
    if (past) return get(`users/${id}/engagement_events.json?past=true`, config)
    return get(`users/${id}/engagement_events.json`, config)
  }

  const getUserParticipatingEventsReadyToCheckin = ({ id }) =>
    get(
      `users/${id}/participating_events_ready_to_checkin`,
      eventSparse(eventCardSparseFields, eventCardSparseInclude),
    )

  const getUserFollowers = ({ id, page, perPage } = {}) => {
    const params = new URLSearchParams()
    if (page != null && page !== '') params.set('page', String(page))
    if (perPage != null && perPage !== '')
      params.set('per_page', String(perPage))
    const qs = params.toString()
    const path = `users/${id}/followers.json${qs ? `?${qs}` : ''}`
    return get(path, userCardSparse)
  }

  const getUserFollowing = ({ id }) =>
    get(`users/${id}/following.json`, userCardSparse)

  const getUserBlocked = () => get('users/blocked', userCardSparse)

  const userFollow = ({ id }) => put(`users/${id}/follow`)

  const userUnfollow = ({ id }) => put(`users/${id}/unfollow`)

  const businessFollow = ({ id }) => put(`businesses/${id}/follow`)

  const businessUnfollow = ({ id }) => put(`businesses/${id}/unfollow`)

  const getBusinessFollowers = ({ id, page, perPage } = {}) => {
    const params = new URLSearchParams()
    if (page != null && page !== '') params.set('page', String(page))
    if (perPage != null && perPage !== '')
      params.set('per_page', String(perPage))
    const qs = params.toString()
    const path = `businesses/${id}/followers.json${qs ? `?${qs}` : ''}`
    return get(path, userCardSparse)
  }

  const charityFollow = ({ id }) => put(`charities/${id}/follow`)

  const charityUnfollow = ({ id }) => put(`charities/${id}/unfollow`)

  const getCharityFollowers = ({ id, page, perPage } = {}) => {
    const params = new URLSearchParams()
    if (page != null && page !== '') params.set('page', String(page))
    if (perPage != null && perPage !== '')
      params.set('per_page', String(perPage))
    const qs = params.toString()
    const path = `charities/${id}/followers.json${qs ? `?${qs}` : ''}`
    return get(path, userCardSparse)
  }

  const userBlock = ({ id }) => put(`users/${id}/block`)

  const userUnblock = ({ id }) => put(`users/${id}/unblock`)

  const submitReport = () => post('reports')

  const deletePhoto = ({ id }) => del(`media_attachments/${id}`)

  const eventLike = ({ id }) => put(`events/${id}/like`)

  const eventUnlike = ({ id }) => put(`events/${id}/unlike`)

  const galleryLike = ({ id }) => put(`media_attachments/${id}/like`)

  const galleryUnlike = ({ id }) => put(`media_attachments/${id}/unlike`)

  const commentLike = ({ id }) => put(`comments/${id}/like`)

  const commentUnlike = ({ id }) => put(`comments/${id}/unlike`)

  const eventSave = ({ id }) => put(`events/${id}/save`)

  const eventUnsave = ({ id }) => put(`events/${id}/unsave`)

  const eventSubscribeToCalendar = ({ id }) =>
    put(`events/${id}/calendar_subscribe`)

  const eventUnsubscribeFromCalendar = ({ id }) =>
    put(`events/${id}/calendar_unsubscribe`)

  const eventShare = ({ id }) => put(`events/${id}/share_count`)

  const eventUnparticipate = ({ id }) => put(`events/${id}/unparticipate.json`)

  const eventParticipate = ({ id }) => put(`events/${id}/participate.json`)

  const getNotifications = () =>
    api
      .get('notifications', {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        params: notificationSparse.params,
      })
      .catch(error => ({
        data: undefined,
        status: error?.response?.status,
        error,
      }))

  const giveLikePlus = ({ id }) => put(`users/${id}/likeplus`)

  const getUnreadNotificationCount = ({ id }) =>
    get(`users/${id}/unread_notifications_count.json`)

  const markAllNotificationsAsRead = () => post('notifications/read_all')

  const deleteAllNotifications = () => del('notifications/clean.json')

  const createMediaAttachment = data => post('media_attachments.json', data)

  const forgotPasswordCode = () => get('api/v1/auth/password/edit')

  const updatePassword = ({
    newPassword,
    confirmPassword,
    resetPasswordToken,
  }) =>
    put('api/v1/auth/password.json', {
      password: newPassword,
      password_confirmation: confirmPassword,
      reset_password_token: resetPasswordToken,
    })

  const eventComment = () => post('comments')

  const editComment = ({ comment }) => put(`comments/${comment.id}`, comment)

  const deleteComment = ({ id }) => del(`comments/${id}`)

  const createEvent = props => post('events.json', props)

  const getEvents = () =>
    get(
      'events.json',
      eventSparse(eventCardSparseFields, eventCardSparseInclude),
    )

  const deleteEvent = id => del(`events/${id}`)

  const getCurrentUserEvents = useCallback(
    () =>
      get(
        `users/${currentUser.slug ?? currentUser.id}/events.json`,
        eventSparse(eventCardSparseFields, eventCardSparseInclude),
      ),
    [get, currentUser],
  )

  const getCurrentUserGroups = useCallback(
    () => get(`users/${currentUser.slug ?? currentUser.id}/groups.json`),
    [get, currentUser],
  )

  // groups for any user (e.g. when viewing another user's profile)
  const getUserGroups = useCallback(
    userSlugOrId => get(`users/${userSlugOrId}/groups.json`),
    [get],
  )

  const getSavedEvents = () =>
    get(
      'events/saved.json',
      eventSparse(eventCardSparseFields, eventCardSparseInclude),
    )

  const getEventsWithParams = params =>
    get('events.json', {
      params: {
        ...params,
        ...eventSparse('id,title,slug', 'media_attachments').params,
      },
    })

  const getSearchedEvents = () =>
    get(
      'events.json',
      eventSparse(eventCardSparseFields, eventCardSparseInclude),
    )

  const getEvent = ({ id }) =>
    get(
      `events/${id}.json`,
      eventSparse(eventDetailSparseFields, eventDetailSparseInclude),
    )

  const updateEvent = (eventId, formData) =>
    put(`events/${eventId}.json`, formData)

  const takeEvent = props => put(`events/${props.id}/take`, props)

  const getHashtag = ({ description }) =>
    get(`events/hashtag/${description}.json`)

  const createAccount = props =>
    post(`api/v1/auth?locale=${props.language}`, {
      device_id: props.deviceId,
      email: props.email,
      language: props.language,
      marketing_opt_in: props.marketing_opt_in,
      name: props.name,
      nickname: props.nickname,
      password_confirmation: props.password,
      password: props.password,
      web_app_build_id: BUILD_NUMBER,
      web_app_version_id: VERSION_NUMBER,
    }).then(response => {
      if (response.ok) {
        if (response.headers && response.headers['access-token']) {
          const authorization = {
            'access-token': response.headers['access-token'],
            'token-type': 'Bearer',
            client: response.headers.client,
            expiry: response.headers.expiry,
            uid: response.headers.uid,
          }
          setAuthHeaders(authorization)
          setCurrentUser(response.data.data)
        }
      }
      return response
    })

  const updateAccount = props => put('api/v1/auth.json', props)

  const forgotPassword = ({ email, locale }) => {
    const body = {
      email,
      redirect_url: 'https://civicrush.com',
    }
    return post(`api/v1/auth/password?locale=${locale}`, body)
  }

  const getMediaAttachmentUploadSignedUrl = data => post('uploads.json', data)

  const getPrivateAttachmentUploadSignedUrl = data => {
    return getMediaAttachmentUploadSignedUrl({
      ...data,
      private: true,
    })
  }

  const uploadFileToS3 = (url, file, contentType) =>
    put(url, file, {
      headers: {
        'Content-Type': contentType,
      },
    })

  const volunteerGet = ({ id }) => get(`events/${id}/volunteers.json`)
  const volunteerCreate = ({ id }) => post(`events/${id}/volunteers.json`)
  const volunteerGetWaitlist = ({ id }) =>
    get(`events/${id}/volunteers/waitlist.json`)
  const volunteerDelete = ({ eventId, volunteerId }) =>
    del(`events/${eventId}/volunteers/${volunteerId}.json`)

  // Emails participant signup report (matches mobile Api.sendEventReport)
  const emailParticipantsList = ({ id }) =>
    get(`events/${id}/email_participants_list.json`)

  // Emails volunteer signup report (matches mobile Api.getVolunteersListReport)
  const emailVolunteersList = ({ id }) =>
    get(`events/${id}/email_volunteers_list.json`)

  const volunteerCheckIn = ({ eventId, volunteerId, checkInTime }) => {
    const body = { check_in_time: checkInTime }
    return put(`events/${eventId}/volunteers/${volunteerId}/check_in`, body)
  }

  const volunteerCheckOut = ({ checkOutTime, eventId, volunteerId }) => {
    const body = { check_out_time: checkOutTime }
    return put(`events/${eventId}/volunteers/${volunteerId}/check_out`, body)
  }

  const volunteerSelfCheckIn = ({ eventId, checkInToken }) =>
    put(`events/${eventId}/volunteers/self_check_in.json`, {
      check_in_token: checkInToken,
    })

  const volunteerSelfCheckOut = ({ eventId, checkInToken }) =>
    put(`events/${eventId}/volunteers/self_check_out.json`, {
      check_in_token: checkInToken,
    })

  const regenerateCheckInToken = ({ eventId }) =>
    put(`events/${eventId}/regenerate_check_in_token.json`)

  const participantCheckIn = ({ checkInTime, eventId, participantId }) => {
    const body = { check_in_time: checkInTime }
    return put(`events/${eventId}/participants/${participantId}/check_in`, body)
  }

  const participantCheckOut = ({ checkOutTime, eventId, participantId }) => {
    const body = { check_out_time: checkOutTime }
    return put(
      `events/${eventId}/participants/${participantId}/check_out`,
      body,
    )
  }

  const sendFeedback = props => post('feedback.json', props)

  const getCivicParticipation = props => {
    if (!props.end_date) {
      const end = new Date()
      props.end_date = `${
        end.getMonth() + 1
      }-${end.getDate()}-${end.getFullYear()}`
    }
    if (!props.start_date) {
      const start = new Date()
      props.start_date = `${start.getMonth() + 1}-${start.getDate()}-${
        start.getFullYear() - 1
      }`
    }
    // was originally: return get(`users/${data.userId}/civic_participation.json`, data);
    return get(
      `users/${props.userId}/civic_participation.json?start_date=${props.start_date}&end_date=${props.end_date}`,
    )
  }

  const getShirtSizes = () => get('shirt_sizes.json')

  const getSkills = () => get('skills.json')

  const getCommunityContributions = () => get('community_contributions.json')

  const requestAccountVerification = () =>
    put('email_verifications/request_code.json')

  const sendAccountVerificationCode = code =>
    put('email_verifications/verify.json', { code })

  // Without both numbers latlng was sent as "undefined,undefined".
  const locationAutocomplete = useCallback(
    ({ input, latitude, longitude }) =>
      get(
        `locations/autocomplete.json?input=${encodeURIComponent(input)}${
          Number.isFinite(latitude) && Number.isFinite(longitude)
            ? `&latlng=${latitude},${longitude}`
            : ''
        }`,
      ),
    [get],
  )

  const locationPlaceDetails = useCallback(
    ({ placeId }) => get(`locations/place_details.json?place_id=${placeId}`),
    [get],
  )

  const charitiesSearch = useCallback(
    ({ query, page, perPage }) =>
      get(`charities/search.json?q=${query}&page=${page}&per_page=${perPage}`),
    [get],
  )

  const createCharityRegistration = useCallback(
    data => post('charity_registrations', data),
    [post],
  )

  const deleteCharityRegistration = useCallback(
    id => del(`charity_registrations/${id}.json`),
    [del],
  )

  const releaseCharityRegistrationEmail = useCallback(
    (id, data) =>
      post(
        `/charity_registrations/${id}/release_charity_registration_email.json`,
        data,
      ),
    [post],
  )

  const getUserCharityRegistrations = useCallback(
    () => get(`charity_registrations.json`),
    [get],
  )

  const getCharity = useCallback(id => get(`charities/${id}.json`), [get])

  const updateCharity = useCallback(
    (id, data) => patch(`charities/${id}.json`, data),
    [patch],
  )

  const getCharityTemplateEmail = useCallback(
    id => get(`/charities/${id}/get_custom_email_body.json`),
    [get],
  )

  const updateCharityTemplateEmail = useCallback(
    (id, data) => post(`/charities/${id}/save_custom_email_body.json`, data),
    [get],
  )

  const getBusiness = useCallback(id => get(`businesses/${id}.json`), [get])

  const getBusinessCompletedSponsoredEvents = useCallback(
    businessId =>
      get(`businesses/${businessId}/completed_sponsored_events.json`),
    [get],
  )

  const getBusinessCommunityMapPins = useCallback(
    businessIdOrSlug =>
      get(`businesses/${businessIdOrSlug}/community_map_pins`),
    [get],
  )

  const getUserCommunityMapPins = useCallback(
    userId => get(`users/${userId}/community_map_pins`),
    [get],
  )

  const updateBusiness = (id, formData) =>
    put(`businesses/${id}.json`, formData)

  const addBusinessAdmin = ({ businessId, userId }) =>
    put(`businesses/${businessId}/add_admin.json`, { user_id: userId })

  const removeBusinessAdmin = ({ businessId, userId }) =>
    put(`businesses/${businessId}/remove_admin.json`, { user_id: userId })

  const createBusiness = useCallback(
    data => post('businesses.json', data),
    [post],
  )

  const getBusinessCivPledges = useCallback(
    (id, params) =>
      get(
        `businesses/${id}/civ_pledges.json?${Object.keys(params)
          .map(key => key + '=' + params[key])
          .join('&')}`,
      ),
    [get],
  )

  const getIndustries = () => get('industries.json')

  const updateCivWallet = useCallback(
    (id, data) => post(`wallets/${id}/fund.json`, data),
    [post],
  )

  const getCivWallet = useCallback(id => get(`wallets/${id}.json`), [get])

  const getUserWallet = useCallback(
    userId => get(`users/${userId}/wallet.json`),
    [get],
  )

  const getBankAccount = useCallback(
    id => get(`bank_accounts/${id}.json`),
    [get],
  )

  const createSponsorshipPledge = useCallback(
    data => post('civ_pledges.json', data),
    [post],
  )

  const createTmpSponsorshipPledge = useCallback(
    data => post('civ_pledges.json', data),
    [post],
  )

  const getAdminFee = useCallback(() => get('admin_fees.json'), [get])

  const getEventsWithPledges = useCallback(
    () => get('civ_pledges/events_with_pledges.json'),
    [get],
  )

  const civPledgeAction = useCallback(
    ({ action, pledgeId, eventId, message }) =>
      post(`/civ_pledges/${pledgeId}/${action}.json`, {
        event_id: eventId,
        message_from_event_owner: message,
      }),
    [post],
  )

  const civPledgeActionTmp = useCallback(
    ({ action, pledgeId, eventId, message }) =>
      post(`/civ_pledges/${pledgeId}/${action}.json`, {
        event_id: eventId,
        message_from_event_owner: message,
      }),
    [post],
  )

  const civPledgeSubmitPayment = useCallback(
    ({ pledgeId, amountPaid }) =>
      post(`/civ_pledges/${pledgeId}/submit_payment.json`, {
        amount_paid: amountPaid,
      }),
    [post],
  )

  const civPledgePay = useCallback(
    ({ pledgeId, amountPaid }) =>
      post(`/civ_pledges/${pledgeId}/pay.json`, {
        amount_paid: amountPaid,
      }),
    [post],
  )

  const civPledgeFinalize = useCallback(
    (id, body = {}) => post(`/civ_pledges/${id}/finalize.json`, body),
    [post],
  )

  const getPledge = id => get(`civ_pledges/${id}.json`)

  const getEventsPayForward = params =>
    get(
      `events/pay_forward_events.json?${Object.keys(params)
        .map(key => key + '=' + params[key])
        .join('&')}`,
    )

  const getEventAcceptedPledges = useCallback(
    id => get(`events/${id}/accepted_civ_pledges.json`),
    [get],
  )

  const updateEmailHeaders = email => {
    const newAuthHeaders = { ...authHeaders, uid: email }
    const newCurrentUser = {
      ...currentUser,
      email,
      uid: email,
    }
    localStorage.setItem('authHeaders', JSON.stringify(newAuthHeaders))
    localStorage.setItem('currentUser', JSON.stringify(newCurrentUser))
    window.location.reload()
  }

  const updateUserInformation = (userId, props) => {
    return api
      .put(`users/${userId}.json`, props)
      .catch(error => ({
        success: false,
        error,
        errors: error?.response?.data?.errors,
      }))
      .then(response => {
        // eslint-disable-next-line no-console
        return response
      })
  }

  const getCivPurchase = useCallback(
    id => get(`civ_purchases/${id}.json`),
    [get],
  )

  const getCivPurchases = params =>
    get(
      `civ_purchases.json?${Object.keys(params)
        .map(key => key + '=' + params[key])
        .join('&')}`,
    )

  const getCharityRegistrations = params =>
    get(
      `charity_registrations/accounting_index.json?${Object.keys(params)
        .map(key => key + '=' + params[key])
        .join('&')}`,
    )

  const getDisbursements = params =>
    get(
      `civ_disbursements/accounting_index.json?${Object.keys(params)
        .map(key => key + '=' + params[key])
        .join('&')}`,
    )

  const getCivillosWallet = (params, format = 'json') =>
    get(
      `civ_purchases/accounting_index.${format}?${Object.keys(params)
        .map(key => key + '=' + params[key])
        .join('&')}`,
    )

  const getMetaWallet = (params, format = 'json') =>
    get(
      `civ_pledges/accounting_index.${format}?${Object.keys(params)
        .map(key => key + '=' + params[key])
        .join('&')}`,
    )

  const getSponsorshipPayments = (params, format = 'json') =>
    get(
      `civ_pledges/sponsorship_payments.${format}?${Object.keys(params)
        .map(key => key + '=' + params[key])
        .join('&')}`,
    )

  const uploadSponsorshipPayment = (id, data) =>
    post(`civ_pledges/${id}/upload_payment_confirmation.json`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

  const updateCharityStatus = (id, data) =>
    patch(`charity_registrations/${id}/update_status.json`, data)

  const uploadReportDisbursement = data =>
    post('civ_disbursement_reports.json', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

  const getCharities = useCallback(() => get('charities.json'), [get])

  const charitiesDonate = useCallback(
    (id, data) => post(`charities/${id}/donate.json`, data),
    [post],
  )

  const getCharityDonations = useCallback(
    id => get(`charities/${id}/donations.json`),
    [get],
  )

  const charitiesSendThankYouEmails = useCallback(
    (id, data) => post(`charities/${id}/send_thank_you_emails.json`, data),
    [post],
  )

  const civDonationResendEmail = useCallback(
    id => post(`civ_donations/${id}/resend_email.json`),
    [post],
  )

  const charitiesMarkAsDisbursed = useCallback(
    (id, data) => post(`civ_disbursements/${id}/mark_as_disbursed.json`, data),
    [post],
  )

  const getCharityDisbursements = useCallback(
    id => get(`charities/${id}/civ_disbursements.json`),
    [get],
  )

  const getDisbursementReport = useCallback(
    id => get(`civ_disbursements/${id}/accounting_show.csv`),
    [get],
  )

  const getSettingPledgeExpirationDays = () =>
    get(`settings/civ_pledge_expiration_days.json?${+new Date()}`)

  const getDefaultMinimumRequiredPledgeAmount = () =>
    get(`settings/default_minimum_required_pledge_amount.json?${+new Date()}`)

  const updateEventVolunteers = (id, params) =>
    put(`events/${id}/volunteers/update_batch.json`, params)

  // Stripe SetupIntent for pledge payment collection
  const createPledgeSetupIntent = (params = {}) =>
    post('civ_pledges/setup_intent.json', params)

  const retryPledgePayment = pledgeId =>
    post(`civ_pledges/${pledgeId}/retry_payment.json`)

  // Stripe Connect for charities
  const createCharityStripeAccount = charityId =>
    post(`charities/${charityId}/create_stripe_account.json`)

  const getCharityStripeOnboardingLink = (charityId, returnUrl, refreshUrl) =>
    get(`charities/${charityId}/stripe_onboarding_link.json`, {
      params: { return_url: returnUrl, refresh_url: refreshUrl },
    })

  const getCharityStripeAccountStatus = charityId =>
    get(`charities/${charityId}/stripe_account_status.json`)

  // Stripe Connect for businesses
  const createBusinessStripeAccount = businessId =>
    post(`businesses/${businessId}/create_stripe_account.json`)

  const getBusinessStripeOnboardingLink = (businessId, returnUrl, refreshUrl) =>
    get(`businesses/${businessId}/stripe_onboarding_link.json`, {
      params: { return_url: returnUrl, refresh_url: refreshUrl },
    })

  const getBusinessStripeAccountStatus = businessId =>
    get(`businesses/${businessId}/stripe_account_status.json`)

  const processCharityPayout = (charityId, disbursementId) =>
    post(
      `charities/${charityId}/civ_disbursements/${disbursementId}/process_payout.json`,
    )

  // Pledge cancel and update payment method
  const cancelPledge = pledgeId => post(`civ_pledges/${pledgeId}/cancel.json`)

  const updatePledgePaymentMethod = (pledgeId, stripePaymentMethodId) =>
    patch(`civ_pledges/${pledgeId}/update_payment_method.json`, {
      stripe_payment_method_id: stripePaymentMethodId,
    })

  return {
    acceptUserFromGroup,
    addEventAdmin,
    addGroupAdmin,
    api,
    authHeaders,
    blockUserFromGroup,
    cancelPledge,
    charitiesDonate,
    charitiesMarkAsDisbursed,
    charitiesSearch,
    charitiesSendThankYouEmails,
    civDonationResendEmail,
    civPledgeAction,
    civPledgeActionTmp,
    civPledgePay,
    civPledgeFinalize,
    civPledgeSubmitPayment,
    commentLike,
    commentUnlike,
    createPledgeSetupIntent,
    createCharityStripeAccount,
    createBusinessStripeAccount,
    createAccount,
    createBusiness,
    createCharityRegistration,
    createEvent,
    createGroup,
    createMediaAttachment,
    createSponsorshipPledge,
    createTmpSponsorshipPledge,
    currentUser,
    declineUserFromGroup,
    del,
    deleteCharityRegistration,
    deleteComment,
    deleteEvent,
    deleteGroup,
    deletePhoto,
    editComment,
    emailAvailable,
    usernameAvailable,
    businessSlugAvailable,
    eventComment,
    eventLike,
    eventParticipate,
    eventSave,
    eventShare,
    eventSubscribeToCalendar,
    eventUnlike,
    eventUnparticipate,
    eventUnsave,
    eventUnsubscribeFromCalendar,
    forgotPassword,
    forgotPasswordCode,
    galleryLike,
    galleryUnlike,
    get,
    getAdminFee,
    getAssociations,
    getBankAccount,
    getBusiness,
    getBusinessCompletedSponsoredEvents,
    getBusinessCommunityMapPins,
    getBusinessCivPledges,
    getBusinessFollowers,
    businessFollow,
    businessUnfollow,
    getCharities,
    getBusinessStripeAccountStatus,
    getBusinessStripeOnboardingLink,
    getCharity,
    getCharityDisbursements,
    getCharityDonations,
    getCharityFollowers,
    getCharityRegistrations,
    charityFollow,
    charityUnfollow,
    getCharityStripeAccountStatus,
    getCharityStripeOnboardingLink,
    getCharityTemplateEmail,
    getCivDonations,
    getCivicParticipation,
    getCivillosWallet,
    getCivPurchase,
    getCivPurchases,
    getCivWallet,
    getCurrentUser,
    getCurrentUserEvents,
    getCurrentUserFollowingEvents,
    getCurrentUserGroupEvents,
    getCurrentUserGroups,
    getUserGroups,
    getDisbursementReport,
    getDisbursements,
    getEvent,
    getEventAcceptedPledges,
    getEventCategories,
    getServiceHourCategories,
    getEvents,
    getEventsPayForward,
    getEventsWithParams,
    getEventsWithPledges,
    getGroup,
    getGroupActivities,
    getGroupEngagementsPDFData,
    getGroupEvents,
    getGroupImpact,
    getGroupMembers,
    getHashtag,
    getIndustries,
    getMediaAttachmentUploadSignedUrl,
    getMetaWallet,
    getMonthlyEvents,
    getMyEvents,
    getMyVolunteering,
    getNotifications,
    getPledge,
    markAllNotificationsAsRead,
    deleteAllNotifications,
    getPossibleAdmins,
    getPrivateAttachmentUploadSignedUrl,
    getSavedEvents,
    getSearchedAssociations,
    getSearchedEvents,
    getDefaultMinimumRequiredPledgeAmount,
    getSettingPledgeExpirationDays,
    getShirtSizes,
    getSkills,
    getCommunityContributions,
    getSponsorshipOpportunities,
    getSponsorshipPayments,
    getTodaysEvents,
    getUnreadNotificationCount,
    getUser,
    getUserBlocked,
    getUserCommunityMapPins,
    getUserCharityRegistrations,
    getUserEngagementEvents,
    getUserEvents,
    getUserFollowers,
    getUserFollowing,
    getUserParticipatingEventsReadyToCheckin,
    getUserSuggestions,
    getUserVolunteeredEvents,
    getUserWallet,
    getVersions,
    giveLikePlus,
    isSessionValid,
    joinAssociation,
    locationAutocomplete,
    locationPlaceDetails,
    participantCheckIn,
    participantCheckOut,
    participantEventRating,
    post,
    processCharityPayout,
    put,
    regenerateCheckInToken,
    releaseCharityRegistrationEmail,
    removeEventAdmin,
    removeGroupAdmin,
    requestAccountVerification,
    requestEventAdminAccess,
    retryPledgePayment,
    search,
    sendAccountVerificationCode,
    sendBlockedReason,
    sendFeedback,
    setCurrentUser,
    signInWithEmail,
    signInWithFacebookResponse,
    signOut,
    submitReport,
    takeEvent,
    unblockUserFromGroup,
    unjoinAssociation,
    updateAccount,
    updateBusiness,
    addBusinessAdmin,
    removeBusinessAdmin,
    updateCharity,
    updateCharityStatus,
    updateCharityTemplateEmail,
    updateCivWallet,
    updateEvent,
    updateEventVolunteers,
    updateGroup,
    updatePassword,
    updatePledgePaymentMethod,
    updateUser,
    updateUserEmail,
    updateUserQuote,
    updateEmailHeaders,
    updateUserInformation,
    uploadFileToS3,
    uploadReportDisbursement,
    uploadSponsorshipPayment,
    userBlock,
    userFollow,
    userUnblock,
    userUnfollow,
    joinGroup,
    leaveGroup,
    volunteerCheckIn,
    volunteerCheckOut,
    volunteerCreate,
    volunteerDelete,
    volunteerEventRating,
    volunteerGet,
    volunteerGetWaitlist,
    volunteerSelfCheckIn,
    volunteerSelfCheckOut,
    volunteerUserRating,
    emailParticipantsList,
    emailVolunteersList,
  }
}

export function ApiProvider({ children }) {
  const api = useApiInternal()
  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>
}

ApiProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export default function useApi() {
  const context = useContext(ApiContext)
  if (context == null) {
    throw new Error('useApi must be used within ApiProvider')
  }
  return context
}
